import {
  FieldErrors,
  UseFieldArrayReturn,
  UseFormRegister,
} from 'react-hook-form';
import {
  AbsoluteCenter,
  Box,
  Divider,
  FormLabel,
  Heading,
  VStack,
} from '@chakra-ui/react';
import { EvaluationEvent } from '@keyops-cep/api-client';

import i18n from '../../../utils/i18n';
import { EvaluationEventEditFormData } from '../evaluation-event-edit-form-utils';

import { EditableNameList } from './EditableNameList';

type ContentSectionProps = {
  register: UseFormRegister<EvaluationEventEditFormData>;
  errors: FieldErrors<EvaluationEventEditFormData>;
  speakersFieldArray: Pick<
    UseFieldArrayReturn<EvaluationEventEditFormData, 'speakers'>,
    'fields' | 'append' | 'remove' | 'move'
  >;
  moderatorsFieldArray: Pick<
    UseFieldArrayReturn<EvaluationEventEditFormData, 'moderators'>,
    'fields' | 'append' | 'remove' | 'move'
  >;
  learningObjectivesFieldArray: Pick<
    UseFieldArrayReturn<EvaluationEventEditFormData, 'learningObjectives'>,
    'fields' | 'append' | 'remove' | 'move'
  >;
  eventData: EvaluationEvent;
};

const ContentSection = ({
  register,
  errors,
  speakersFieldArray,
  moderatorsFieldArray,
  learningObjectivesFieldArray,
  eventData,
}: ContentSectionProps) => {
  return (
    <>
      <Box position="relative" padding="10">
        <Divider />
        <AbsoluteCenter bg="white" px="4">
          <Heading size="md">
            {i18n.t('evaluationEvent.form.content.title')}
          </Heading>
        </AbsoluteCenter>
      </Box>
      <VStack spacing={4} align="stretch" mb={6}>
        <Box>
          <FormLabel fontWeight="bold" mb={1}>
            {i18n.t('evaluationEvent.form.content.fields.topic')}
          </FormLabel>
          {Array.isArray(eventData.topics) && (
            <Box borderWidth={1} borderRadius="md" overflowX="auto" mb={4}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f7fafc' }}>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px',
                        borderBottom: '1px solid #e2e8f0',
                      }}
                    >
                      {i18n.t('evaluationEvent.form.content.fields.code')}
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px',
                        borderBottom: '1px solid #e2e8f0',
                      }}
                    >
                      {i18n.t('evaluationEvent.form.content.fields.name')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {eventData.topics.map((topic) => (
                    <tr key={topic.code}>
                      <td
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #e2e8f0',
                        }}
                      >
                        {topic.code}
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #e2e8f0',
                        }}
                      >
                        {topic.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>

        <EditableNameList<EvaluationEventEditFormData, 'learningObjectives'>
          label="Learning objectives"
          listItemLabel="Objective"
          name="learningObjectives"
          fields={learningObjectivesFieldArray.fields}
          register={register}
          errors={errors}
          append={learningObjectivesFieldArray.append}
          remove={learningObjectivesFieldArray.remove}
          move={learningObjectivesFieldArray.move}
        />

        <EditableNameList<EvaluationEventEditFormData, 'speakers'>
          label="Speakers"
          listItemLabel="Speaker"
          name="speakers"
          fields={speakersFieldArray.fields}
          register={register}
          errors={errors}
          append={speakersFieldArray.append}
          remove={speakersFieldArray.remove}
          move={speakersFieldArray.move}
        />

        <EditableNameList<EvaluationEventEditFormData, 'moderators'>
          label="Moderators"
          listItemLabel="Moderator"
          name="moderators"
          fields={moderatorsFieldArray.fields}
          register={register}
          errors={errors}
          append={moderatorsFieldArray.append}
          remove={moderatorsFieldArray.remove}
          move={moderatorsFieldArray.move}
        />
      </VStack>
    </>
  );
};

export default ContentSection;
